// Phase 31 (plan 02): reusable message list with date dividers and optional
// ticket-id dividers (technician miniapp only — gated by showTicketDividers).
//
// The Bayon embed (plan 04) leaves showTicketDividers false; messages render
// as a single chronological stream with date dividers only. ticketId is then
// treated as opaque metadata everywhere else in the chat surface — see the
// INVARIANT comment in MessageBubble.tsx and MediaContent.tsx.

import { Fragment, useRef, useState } from 'react';
import { AlertTriangle, Check, CornerUpLeft, Megaphone, Play, RefreshCw } from 'lucide-react';

// ── Announcement helpers ──────────────────────────────────────────────────────
// Broadcast messages are recorded as "title\n\nbody [label](url)"
// (buildBroadcastRecordText). Parse those parts so the chat card can render a
// bold title, body with an inline bracketed link, and a video thumbnail.
function parseAnnouncement(text: string): {
  title: string;
  body: string;
  link: string | null;
  label: string;
} {
  const raw = text ?? '';
  let link: string | null = null;
  let label = 'Video Link';
  let rest = raw;
  // Preferred format: a trailing markdown-style [label](url) at the very end.
  const md = raw.match(/\s*\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)\s*$/);
  if (md) {
    label = md[1];
    link = md[2];
    rest = raw.slice(0, md.index).trimEnd();
  }
  const parts = rest.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  // Legacy format: a bare URL as the last paragraph.
  if (!link && parts.length > 1 && /^https?:\/\/\S+$/i.test(parts[parts.length - 1])) {
    link = parts.pop() ?? null;
  }
  const title = parts.shift() ?? '';
  const body = parts.join('\n\n');
  return { title, body, link, label };
}

function youtubeThumbnail(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    let id = '';
    if (host === 'youtu.be') id = u.pathname.slice(1).split('/')[0] ?? '';
    else if (host.endsWith('youtube.com')) {
      id = u.searchParams.get('v') ?? (u.pathname.match(/^\/(?:embed|shorts|v)\/([\w-]{11})/)?.[1] ?? '');
    }
    return /^[\w-]{11}$/.test(id) ? `https://i.ytimg.com/vi/${id}/mqdefault.jpg` : null;
  } catch {
    return null;
  }
}
import ReactMarkdown from 'react-markdown';
import type { Message } from '../../types';
import type { ExternalContext } from '../../services/api';
import { MessageBubble } from './MessageBubble';
import { useLongPress } from '../../hooks/useLongPress';
import { useSwipeReply } from '../../hooks/useSwipeReply';

// ---------------------------------------------------------------------------
// SimVerifier initDataDetails person card (dynamic render).
//
// The partner ships an open-ended `initDataDetails` object on a support case
// (address, names, profileUrl, legalId, ...). We render it GENERICALLY: iterate
// whatever keys exist so the UI survives the partner adding / renaming keys and
// works for ANY case type. The ONLY static part is a small case-insensitive
// DENYLIST; everything else shows.
// ---------------------------------------------------------------------------

/** Keys never shown (case-insensitive). Identifiers / opaque internals. */
const INIT_DATA_DENYLIST = new Set(
  ['hash', 'deviceId', 'userId', 'providerId', 'requestId', 'timeStamp'].map((k) =>
    k.toLowerCase()
  )
);

/** Acronyms uppercased whole when they appear as a standalone word part. */
const ACRONYMS: Record<string, string> = { id: 'ID', url: 'URL' };

/**
 * Turn a partner key into a human label. Splits camelCase + snake_case +
 * kebab-case into words, Title-Cases each, uppercases known acronyms.
 * e.g. dateOfBirth → "Date of Birth", legalId → "Legal ID",
 *      displayName → "Display Name", profile_url → "Profile URL".
 * Small connector words stay lowercase when interior (of/the/and/to).
 */
function prettifyKey(key: string): string {
  const words = key
    // camelCase / PascalCase boundary
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    // snake_case + kebab-case
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const CONNECTORS = new Set(['of', 'the', 'and', 'to', 'in', 'for']);
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (ACRONYMS[lower]) return ACRONYMS[lower];
      if (i > 0 && CONNECTORS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/** Render any value to a display string. Never throws. */
function renderInitDataValue(value: unknown): string {
  try {
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (value === null || value === undefined) return '';
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** A value is worth showing when it isn't null/undefined/empty-string. */
function hasDisplayableValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}

/** True when the object has at least one VISIBLE key after the denylist. */
function hasVisibleInitData(data: Record<string, unknown>): boolean {
  return Object.entries(data).some(
    ([k, v]) => !INIT_DATA_DENYLIST.has(k.toLowerCase()) && hasDisplayableValue(v)
  );
}

/**
 * Collapsible "Show user details" person card. Renders ONLY when `data` has at
 * least one visible key. Iterates whatever keys exist (denylist-filtered);
 * profileUrl becomes a small avatar; every other entry is a label/value row
 * reusing the yellow-box field styling. Robust to unknown / malformed shapes.
 */
function UserDetailsCard({ data }: { data: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);

  // Separate profileUrl (rendered as an avatar) from the label/value rows.
  const profileUrlEntry = Object.entries(data).find(
    ([k, v]) => k.toLowerCase() === 'profileurl' && typeof v === 'string' && v.trim() !== ''
  );
  const profileUrl = profileUrlEntry ? (profileUrlEntry[1] as string) : null;

  const rows = Object.entries(data).filter(
    ([k, v]) =>
      !INIT_DATA_DENYLIST.has(k.toLowerCase()) &&
      k.toLowerCase() !== 'profileurl' &&
      hasDisplayableValue(v)
  );

  return (
    <div className="mt-2 pt-2 border-t border-yellow-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[12px] font-medium text-amber-800 hover:text-amber-900"
      >
        {open ? 'Hide user details ▴' : 'Show user details ▾'}
      </button>
      {open ? (
        <div className="mt-2 text-[12px] text-gray-800 leading-relaxed">
          {profileUrl ? (
            <div className="mb-2">
              <img
                src={profileUrl}
                alt="User photo"
                className="h-12 w-12 rounded-full object-cover border border-yellow-200"
                onError={(e) => {
                  // Graceful fallback: swap the broken image for a "Photo" link.
                  const img = e.currentTarget;
                  const link = document.createElement('a');
                  link.href = profileUrl;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  link.textContent = 'Photo';
                  link.className = 'text-[#007AFF] underline text-[12px]';
                  img.replaceWith(link);
                }}
              />
            </div>
          ) : null}
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            {rows.map(([key, value]) => (
              <Fragment key={key}>
                <span className="text-gray-500 whitespace-nowrap">{prettifyKey(key)}</span>
                <span className="min-w-0 font-medium text-gray-900 break-words [overflow-wrap:anywhere] whitespace-pre-line">
                  {renderInitDataValue(value)}
                </span>
              </Fragment>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** v4.1 wrapper around a single message bubble. Adds the long-press gesture
 *  (opens the action modal for single-message delete) and, when
 *  selectionMode is on, intercepts taps to toggle selection instead of
 *  letting the bubble's own click handlers fire (media open, etc.).
 *  A tinted overlay indicates selection state.
 *
 *  iOS callout suppression: `WebkitUserSelect/touchCallout/tap-highlight`
 *  are zeroed so the OS doesn't pop its "Copy / Look Up / Translate"
 *  text-selection menu when a long-press lands on text inside the bubble.
 *  We can't rely on onContextMenu.preventDefault alone — iOS Safari fires
 *  the OS selection UI from a parallel gesture pipeline.
 *
 *  On long-press fire, we capture three things and forward them to the
 *  parent so it can render a faithful "lifted" clone of the bubble above
 *  the action modal's backdrop (Telegram convention): the bubble's
 *  bounding rect, the bubble's outerHTML, and the isMine side flag so the
 *  modal anchors itself to the correct edge. */
function SelectableMessageWrapper({
  messageId,
  selectionMode,
  isSelectable,
  isSelected,
  isReplyable,
  onLongPress,
  onToggleSelection,
  onSwipeReply,
  children,
}: {
  messageId: number;
  selectionMode: boolean;
  /** v4.1: true iff this row can be deleted (technician-sent + has
   *  telegram_message_id + ≤48 h old). Ineligible rows show no checkbox
   *  and ignore tap-toggle in selection mode — they're greyed out and
   *  effectively skipped. The long-press path still works in default
   *  mode (the action modal then explains why Delete is disabled). */
  isSelectable: boolean;
  isSelected: boolean;
  /** Phase 33: true iff this row has a non-null telegram_message_id and
   *  can therefore be the target of a quoted reply. Gates the
   *  swipe-left-to-reply gesture (and mirrors the modal Reply button's
   *  visibility). Historical rows pre-migration-019 are not replyable. */
  isReplyable: boolean;
  onLongPress?: (anchorRect: DOMRect, bubbleHtml: string) => void;
  onToggleSelection?: () => void;
  /** Phase 33: fired when the user releases a left-swipe past the
   *  trigger threshold. Caller seeds the reply-target state (same path
   *  as the modal's Reply action). */
  onSwipeReply?: () => void;
  children: React.ReactNode;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const lp = useLongPress({
    delayMs: 500,
    onLongPress: () => {
      // Skip the long-press path in selection mode — tap toggles instead.
      if (selectionMode) return;
      if (onLongPress && wrapperRef.current) {
        // The wrapper div spans the full row width because MessageBubble's
        // outer container uses flex justify-start/end to push the bubble
        // to one side. We want the actual bubble's rect (the `max-w-[80%]`
        // container marked with data-message-bubble) so the modal anchors
        // to the bubble itself, not to the empty space on the other side.
        const bubbleEl = wrapperRef.current.querySelector('[data-message-bubble]') as HTMLElement | null;
        const target = bubbleEl ?? wrapperRef.current;
        const rect = target.getBoundingClientRect();
        const html = target.outerHTML;
        onLongPress(rect, html);
      }
    },
  });
  // Phase 33: swipe-left-to-reply. Disabled in selection mode (the
  // selection-mode tap-handler owns the gesture) and when the row has
  // no telegram_message_id to quote against. The cancel callback just
  // clears the long-press timer — useLongPress.onPointerCancel ignores
  // its event arg, so calling it with no args is safe.
  const swipe = useSwipeReply({
    enabled: !selectionMode && isReplyable && !!onSwipeReply,
    onSwipeReply: () => onSwipeReply?.(),
    cancelLongPress: () => lp.onPointerCancel(undefined as never),
  });
  const handleClick = (e: React.MouseEvent) => {
    if (lp.wasLongPressTriggeredRef.current) {
      e.preventDefault();
      e.stopPropagation();
      lp.wasLongPressTriggeredRef.current = false;
      return;
    }
    // In selection mode, only eligible rows can be toggled. Ineligible
    // rows swallow the tap so the bubble's own click handlers (media open,
    // etc.) don't fire either — selection mode is exclusive.
    if (selectionMode) {
      e.preventDefault();
      e.stopPropagation();
      if (isSelectable && onToggleSelection) onToggleSelection();
    }
  };
  // Combine the pointer handlers from both gestures: long-press first,
  // then swipe. Order matters only on down/cancel/up — the move handler
  // arbitrates direction commit between them.
  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    lp.onPointerDown(e);
    swipe.onPointerDown(e);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    lp.onPointerMove(e);
    swipe.onPointerMove(e);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLElement>) => {
    lp.onPointerUp(e);
    swipe.onPointerUp(e);
  };
  const onPointerCancel = (e: React.PointerEvent<HTMLElement>) => {
    lp.onPointerCancel(e);
    swipe.onPointerCancel(e);
  };
  return (
    <div
      ref={wrapperRef}
      data-message-id={messageId}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={lp.onPointerLeave}
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: 'relative',
        cursor: selectionMode && isSelectable ? 'pointer' : undefined,
        // Suppress the iOS text-selection callout that otherwise pops up
        // mid-long-press. WebkitUserSelect alone isn't enough — touchCallout
        // is the actual "Copy / Look Up / Translate" UI; tap-highlight-color
        // kills the grey iOS tap flash that follows the long-press.
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent',
        // Dim ineligible rows in selection mode so the technician can see
        // at a glance which messages they can't act on.
        opacity: selectionMode && !isSelectable ? 0.45 : 1,
        // Prevent horizontal pan from triggering browser back-swipe while
        // we own the gesture. Vertical scroll stays intact.
        touchAction: 'pan-y',
      }}
    >
      {/* Phase 33: reply-icon overlay revealed by left-swipe. Sits on the
          RIGHT edge of the row, fades + scales as the swipe approaches
          the trigger threshold. Snap to "armed" color past threshold. */}
      {swipe.translateX < 0 ? (
        <span
          aria-hidden
          className="absolute top-1/2 -translate-y-1/2 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full pointer-events-none"
          style={{
            background: swipe.armed ? '#007AFF' : 'rgba(0,122,255,0.18)',
            color: swipe.armed ? '#FFFFFF' : '#007AFF',
            opacity: swipe.progress,
            transform: `scale(${0.6 + swipe.progress * 0.4})`,
            transition: 'background 80ms linear, color 80ms linear',
          }}
        >
          <CornerUpLeft className="w-5 h-5" strokeWidth={2.5} />
        </span>
      ) : null}
      {/* Translate the actual row content while leaving the icon overlay
          pinned to the right edge of the wrapper. Spring-back is purely
          CSS — the hook resets translateX to 0 on pointerup/cancel and the
          transition handles the animation. */}
      <div
        style={{
          transform: `translateX(${swipe.translateX}px)`,
          transition:
            swipe.translateX === 0
              ? 'transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)'
              : undefined,
        }}
      >
        {selectionMode ? (
          // Telegram-style multi-select row: a 22px checkbox circle on the
          // far LEFT regardless of bubble alignment, then the bubble row
          // takes the remaining width with its own justify-start/end intact.
          // No bubble tint — the checkbox is the only selection indicator.
          // Ineligible rows reserve the same 22px gutter so columns stay
          // aligned, but render an empty placeholder instead of a checkbox.
          <div className="flex items-center gap-2 pl-3">
            {isSelectable ? (
              <SelectionCheckbox selected={isSelected} />
            ) : (
              <span aria-hidden className="inline-block w-[22px] h-[22px] flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">{children}</div>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

/** 22px circular checkbox shown on the left of every row in selection mode.
 *  Telegram convention: ring outline when unchecked, filled blue with a
 *  white check when selected. Visual-only — the parent row absorbs taps. */
function SelectionCheckbox({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full flex-shrink-0 transition-colors"
      style={{
        background: selected ? '#007AFF' : 'transparent',
        border: selected ? 'none' : '1.5px solid rgba(60,60,67,0.36)',
      }}
    >
      {selected ? <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} /> : null}
    </span>
  );
}

// Maps external_source values to short display labels.
// 'SV' is what the real SimVerifier API sends; 'simverifier' is the legacy mock value.
const SOURCE_LABEL: Record<string, string> = {
  simverifier: 'SV',
  SV: 'SV',
};
function sourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? source.toUpperCase();
}

// Phase 36: a "case type" chip shown for case shapes that aren't a plain
// pending request — derived from the referenceId prefix, which is the stable
// signal (the source stays 'SV'). SimVerifier general-failure cases use the
// `sv_gf_` prefix and render as "Failed Activity". Returns null for ordinary
// cases so no chip renders. Extend as new shapes appear.
function caseTypeChip(referenceId?: string | null): string | null {
  if (!referenceId) return null;
  if (referenceId.toLowerCase().startsWith('sv_gf_')) return 'Failed Activity';
  return null;
}

/** Renders a ticket id with the `TICK-` prefix de-emphasized so the unique
 *  digits dominate the reader's eye. At 11px on a small pill, TICK-92461 vs
 *  TICK-92464 vs TICK-92465 blur into the same word otherwise — and when
 *  the divider lines them up vertically it becomes impossible to tell at a
 *  glance how many distinct active tickets are in front of you. */
function TicketIdLabel({ ticketId }: { ticketId: string }) {
  const dashIdx = ticketId.indexOf('-');
  if (dashIdx < 0) return <>{ticketId}</>;
  const prefix = ticketId.slice(0, dashIdx + 1);
  const suffix = ticketId.slice(dashIdx + 1);
  return (
    <>
      <span style={{ opacity: 0.55 }}>{prefix}</span>
      {suffix}
    </>
  );
}

function formatDay(timestamp: string): string {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Hold duration (ms) before a ticket divider press triggers selection mode.
 *  500ms is the iOS / Android long-press convention — short enough to feel
 *  intentional, long enough that a brisk scroll-tap doesn't trigger it. */
const TICKET_LONG_PRESS_MS = 500;

/** Press-and-hold version of the ticket divider chip. Wraps the original
 *  chip in a button that fires `onLongPress` only when the pointer is held
 *  past TICKET_LONG_PRESS_MS — short taps do nothing, so casual scrolling
 *  over the divider never accidentally enters selection mode.
 *
 *  Inline component (not a separate file) because it's tightly coupled to
 *  the divider render below and lives inside the .map — extracting just to
 *  satisfy "no hooks in map" without splitting it across files. */
function LongPressDividerButton({
  ticketId,
  onLongPress,
  children,
}: {
  ticketId: string;
  onLongPress: () => void;
  children: React.ReactNode;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return (
    <button
      type="button"
      onPointerDown={() => {
        firedRef.current = false;
        clear();
        timerRef.current = setTimeout(() => {
          firedRef.current = true;
          onLongPress();
        }, TICKET_LONG_PRESS_MS);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onContextMenu={(e) => {
        // iOS long-press surfaces the OS callout (Copy / Define / etc.).
        // We've already captured the gesture — block it.
        e.preventDefault();
      }}
      onClick={(e) => {
        // If the long-press fired, the click that follows the release is
        // redundant — and would briefly look like a tap-action. Swallow it.
        if (firedRef.current) {
          e.preventDefault();
          firedRef.current = false;
        }
      }}
      aria-label={`Hold to select ${ticketId} for closing`}
      className="touch-active rounded-full"
      style={{
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
    >
      {children}
    </button>
  );
}

export interface MessageListProps {
  messages: Message[];
  /**
   * Used by the technician miniapp shim as the fallback label above LEFT-side
   * (incoming) bubbles when the caller does not provide getSenderLabel /
   * senderLabel. Bayon embed passes its viewer's first name here (or anything,
   * since it will likely provide getSenderLabel instead).
   */
  userFirstName: string;
  /**
   * NEW (plan 02): when true, render TICK-XXXX dividers between messages
   * whose ticketId changes. Default false — Bayon embed leaves this off.
   * Technician miniapp shim flips it true to preserve existing behavior.
   */
  showTicketDividers?: boolean;
  /**
   * NEW (plan 02): caller-supplied per-message sender label resolver. When
   * provided, the result is passed to MessageBubble as `senderLabel` and used
   * verbatim above left-side bubbles. When omitted, MessageBubble falls back
   * to the technician miniapp's userFirstName / message.technicianName logic.
   * Called with (message, isFromUser) so the Bayon adapter can produce a
   * sender-identity-based label without inferring from the message's role.
   */
  getSenderLabel?: (message: Message, isFromUser: boolean) => string;
  /**
   * Identity-based "me" derivation (technician miniapp use case). When set
   * to a non-empty string, a message is "mine" iff `msg.from === 'technician'`
   * AND `String(msg.technicianId) === viewerDeviceId`. Other technicians'
   * messages then render on the LEFT with `message.technicianName` as the
   * sender label — Telegram/iMessage convention extended to multi-technician
   * chats. When empty or omitted, falls back to role-based isMine derivation
   * (msg.from !== 'user'), preserving today's single-technician behavior and
   * keeping the Bayon embed adapter path unchanged.
   */
  viewerDeviceId?: string;
  /**
   * Explicit override for whether right-side ("mine") bubbles get a sender
   * label. When omitted, defaults to `!!viewerDeviceId` (i.e. label-on-mine
   * iff identity-based positioning is on). The technician miniapp passes
   * `true` even when viewerDeviceId is empty so labels render on every
   * bubble in role-based miniapp mode too — letting agents tell their own
   * messages apart from other technicians' even though both go on the right.
   */
  showLabelOnMine?: boolean;
  // Phase 32 inline-divider context — all optional; only consumed by the
  // gated showTicketDividers branch.
  ticketsExternal?: Record<string, ExternalContext>;
  onRefreshTicketExternal?: (ticketId: string) => void;
  refreshingTicketIds?: Set<string>;
  failedRefreshTicketIds?: Set<string>;
  // Multi-select close (technician miniapp only). When `selectionMode` is
  // true and a divider's ticketId is in `selectableTicketIds`, the chip
  // becomes a tappable checkbox toggle. Closed tickets (not in
  // selectableTicketIds) render as plain chips — visible context, not
  // selectable.
  selectionMode?: boolean;
  selectedTicketIds?: Set<string>;
  selectableTicketIds?: Set<string>;
  onToggleTicketSelection?: (ticketId: string) => void;
  /** Tap-to-enter-selection on a default-mode divider. When provided AND
   *  the ticket is in `selectableTicketIds`, the chip becomes a button that
   *  fires this handler. Lets the user discover multi-select by touching
   *  any open ticket — no dedicated Select button in the header. */
  onTicketDividerTap?: (ticketId: string) => void;
  /** Phase 34 (L2 reopen): long-press a CLOSED-ticket divider chip to fire
   *  this handler. Inverse of `onTicketDividerTap` (which targets open
   *  tickets for multi-select close). Caller is expected to surface a
   *  reason-input modal and call POST /api/tickets/:id/reopen. Pass undefined
   *  to keep closed dividers as plain read-only chrome (L1 users, embed
   *  surfaces, etc.). */
  onTicketDividerLongPressForReopen?: (ticketId: string) => void;
  /** Always-compact closed-ticket sections. When true, contiguous runs of
   *  messages belonging to tickets NOT in `selectableTicketIds` collapse
   *  into a single tappable "N closed tickets" line — so a long history of
   *  resolved cases doesn't visually drown the still-active ones. Tap the
   *  line to expand that run inline (in-component state). Skipped when the
   *  conversation has zero open tickets (collapsing everything is useless). */
  compactClosedTickets?: boolean;
  // v4.1 — message-level multi-select & long-press. All optional; absent
  // for the Bayon embed surface, present in the technician miniapp.
  /** Fired when the user holds a message bubble for ~500 ms. Single-message
   *  delete path: open the action modal scoped to this message. The
   *  callback receives the bubble's bounding rect, its outerHTML at
   *  gesture time (used to render a faithful clone above the backdrop),
   *  and the isMine side flag (so the modal anchors to the right edge for
   *  technician-sent bubbles, left for incoming). */
  onMessageLongPress?: (
    message: Message,
    anchorRect: DOMRect,
    bubbleHtml: string,
    isMine: boolean
  ) => void;
  /** When true, every bubble becomes a tap-target that toggles selection
   *  via `onToggleMessageSelection`. The bubble's normal tap action (e.g.
   *  open media) is suppressed in this mode. */
  messageSelectionMode?: boolean;
  /** Selected message IDs — bubbles in this set render with a tint overlay. */
  selectedMessageIds?: Set<number>;
  /** Toggle a message in/out of the selection. */
  onToggleMessageSelection?: (messageId: number) => void;
  /** Phase 33: fires when the user releases a left-swipe past the trigger
   *  threshold on a replyable message. Callee seeds the reply-target state
   *  (the same state set by the modal Reply action). Only invoked for rows
   *  with a non-null telegram_message_id; historical pre-capture rows
   *  never fire this. Absent → no swipe gesture is wired. */
  onMessageSwipeReply?: (message: Message) => void;
  /** Phase 33: fires when the user taps the in-bubble quoted-preview
   *  sub-element ("Replying to <X>"). Receives the target's DB id;
   *  caller scrolls that message into view (and ideally flashes a
   *  highlight). Absent → preview renders as plain non-tappable text. */
  onJumpToMessage?: (targetMessageId: number) => void;
  /** Fires the first time the user taps to expand a closed-group placeholder.
   *  TicketDetail uses this to release its pagination gate — by expanding,
   *  the user has explicitly opted into seeing closed-ticket history, so
   *  scrolling further up should keep loading older messages. */
  onExpandClosedGroup?: () => void;
}

export function MessageList({
  messages,
  userFirstName,
  showTicketDividers = false,
  getSenderLabel,
  viewerDeviceId,
  showLabelOnMine,
  ticketsExternal,
  onRefreshTicketExternal,
  refreshingTicketIds,
  failedRefreshTicketIds,
  selectionMode = false,
  selectedTicketIds,
  selectableTicketIds,
  onToggleTicketSelection,
  onTicketDividerTap,
  onTicketDividerLongPressForReopen,
  compactClosedTickets = false,
  onMessageLongPress,
  messageSelectionMode = false,
  selectedMessageIds,
  onToggleMessageSelection,
  onMessageSwipeReply,
  onJumpToMessage,
  onExpandClosedGroup,
}: MessageListProps) {
  // Which closed-group placeholders the user has tapped to expand. Local
  // state — resets when the conversation unmounts, which matches the
  // "fresh chat → fresh collapse" expectation. Group identity is the
  // joined ticket-ID list, so adding a new closed ticket to the run
  // produces a new key and re-collapses (acceptable: the run changed).
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set()
  );
  const toggleGroup = (key: string) => {
    const willExpand = !expandedGroups.has(key);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (willExpand) onExpandClosedGroup?.();
  };
  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <span className="text-5xl" style={{ opacity: 0.45 }}>💬</span>
        <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.75)' }}>
          No messages yet
        </p>
      </div>
    );
  }

  type ListItem =
    | { kind: 'date'; label: string; key: string }
    | { kind: 'ticket'; ticketId: string; key: string }
    | { kind: 'msg'; msg: Message; origIdx: number }
    | { kind: 'closed-group'; ticketIds: string[]; messageCount: number; key: string };

  const items: ListItem[] = [];
  let lastDay = '';
  let lastTicketId = '';

  // Sentinel used by the server when a ticket is auto-created from a /start
  // <ref> deeplink. The row anchors the TICK-XXXX divider; the bubble itself
  // is suppressed here. Kept in sync with REFERRAL_INIT_PLACEHOLDER in
  // server/src/handlers/webhook.ts — change both together.
  const REFERRAL_INIT_PLACEHOLDER = '__REFERRAL_INIT__';

  messages.forEach((msg, idx) => {
    // Ticket boundary divider — shown only when messages span multiple tickets
    // AND the consumer opted in via showTicketDividers. Only reader of
    // msg.ticketId in the chat surface (see the INVARIANT in MessageBubble.tsx).
    if (showTicketDividers && msg.ticketId && msg.ticketId !== lastTicketId) {
      items.push({ kind: 'ticket', ticketId: msg.ticketId, key: `t-${msg.ticketId}` });
      lastTicketId = msg.ticketId;
    }
    // Skip the system placeholder row — divider already pushed above.
    if (msg.text === REFERRAL_INIT_PLACEHOLDER) {
      return;
    }
    const day = new Date(msg.timestamp).toDateString();
    if (day !== lastDay) {
      items.push({ kind: 'date', label: formatDay(msg.timestamp), key: `d-${day}` });
      lastDay = day;
    }
    items.push({ kind: 'msg', msg, origIdx: idx });
  });

  // Always-compact pass. Walks the built items array and collapses
  // contiguous runs of closed-ticket sections (sections whose ticketId is
  // NOT in selectableTicketIds) into a single 'closed-group' placeholder.
  // A "section" is a ticket-divider plus every date/msg item up to the
  // next ticket-divider — that whole chunk belongs to one ticket.
  //
  // Skipped when:
  //  - compactClosedTickets is off (Bayon embed default).
  //  - selectableTicketIds is missing/empty (no anchor for open-vs-closed).
  //  - the conversation has zero OPEN sections (collapsing everything into
  //    a single placeholder leaves nothing to read — the user opened this
  //    chat precisely to see closed history).
  const shouldCompact =
    compactClosedTickets &&
    !!selectableTicketIds &&
    selectableTicketIds.size > 0 &&
    items.some(
      (it) => it.kind === 'ticket' && selectableTicketIds.has(it.ticketId)
    );

  let finalItems: ListItem[] = items;
  if (shouldCompact) {
    const compacted: ListItem[] = [];
    let run: { ticketIds: string[]; messageCount: number; itemsRun: ListItem[] } | null = null;
    const flush = () => {
      if (!run) return;
      const key = `cg-${run.ticketIds.join('-')}`;
      compacted.push({
        kind: 'closed-group',
        ticketIds: run.ticketIds,
        messageCount: run.messageCount,
        key,
      });
      // Expanded groups keep the placeholder (as a tappable "Hide" line)
      // AND emit the original items below it, so the user can re-collapse.
      if (expandedGroups.has(key)) {
        compacted.push(...run.itemsRun);
      }
      run = null;
    };
    let inClosedSection = false;
    for (const it of items) {
      if (it.kind === 'ticket') {
        const open = selectableTicketIds!.has(it.ticketId);
        if (open) {
          flush();
          compacted.push(it);
          inClosedSection = false;
        } else {
          if (!run) run = { ticketIds: [], messageCount: 0, itemsRun: [] };
          run.ticketIds.push(it.ticketId);
          run.itemsRun.push(it);
          inClosedSection = true;
        }
      } else if (inClosedSection && run) {
        run.itemsRun.push(it);
        if (it.kind === 'msg') run.messageCount++;
      } else {
        compacted.push(it);
      }
    }
    flush();

    // A collapsed closed-group already reads as an intermission marker, so
    // re-emitting the SAME open ticket's divider on the other side of it is
    // redundant — the user just sees "TICK-92461 → 5 closed → TICK-92461"
    // and assumes the latter is a different ticket they missed. Drop the
    // trailing divider when the ticket-id matches the one preceding the
    // collapsed group. Expanded groups keep both dividers (the user has
    // walked through the closed history and needs the re-entry cue).
    for (let i = compacted.length - 1; i >= 1; i--) {
      const cur = compacted[i];
      if (cur.kind !== 'ticket') continue;
      let crossedCollapsedGroup = false;
      let prevTicketId: string | null = null;
      for (let j = i - 1; j >= 0; j--) {
        const prev = compacted[j];
        if (prev.kind === 'closed-group' && !expandedGroups.has(prev.key)) {
          crossedCollapsedGroup = true;
        } else if (prev.kind === 'ticket') {
          prevTicketId = prev.ticketId;
          break;
        }
      }
      if (crossedCollapsedGroup && prevTicketId === cur.ticketId) {
        compacted.splice(i, 1);
      }
    }

    finalItems = compacted;
  }

  return (
    <div className="py-2">
      {finalItems.map((item) => {
        if (item.kind === 'ticket') {
          const external = ticketsExternal?.[item.ticketId];
          return (
            <div key={item.key}>
              {/* TICK-XXXX divider chip. In selection mode the chip turns into
                  a checkbox-style toggle for tickets that are still active
                  (selectableTicketIds). Already-closed tickets render as a
                  greyed-out chip — visible for history context, not tappable.
                  Default mode preserves the original render exactly. */}
              {selectionMode ? (() => {
                const selectable = selectableTicketIds?.has(item.ticketId) ?? false;
                const selected = selectedTicketIds?.has(item.ticketId) ?? false;
                return (
                  <div className="flex items-center gap-2 px-4 py-3">
                    <div className="flex-1 h-px" style={{ background: 'rgba(60,60,67,0.18)' }} />
                    <button
                      type="button"
                      onClick={selectable && onToggleTicketSelection
                        ? () => onToggleTicketSelection(item.ticketId)
                        : undefined}
                      disabled={!selectable}
                      aria-pressed={selected}
                      aria-label={
                        selectable
                          ? `${selected ? 'Deselect' : 'Select'} ${item.ticketId}`
                          : `${item.ticketId} (already closed)`
                      }
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full select-none transition disabled:cursor-default"
                      style={{
                        background: selected
                          ? '#007AFF'
                          : selectable
                            ? 'rgba(0,122,255,0.18)'
                            : 'rgba(60,60,67,0.18)',
                        color: selected ? '#FFFFFF' : selectable ? '#007AFF' : 'rgba(60,60,67,0.6)',
                        backdropFilter: 'blur(2px)',
                      }}
                    >
                      {/* Checkbox glyph — filled circle when selected, ring
                          when selectable, lock-ish dot when closed. Keeps
                          the divider compact at the same height as default. */}
                      <span
                        aria-hidden
                        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[10px]"
                        style={{
                          background: selected ? '#FFFFFF' : 'transparent',
                          border: selected
                            ? 'none'
                            : selectable
                              ? '1.5px solid #007AFF'
                              : '1.5px solid rgba(60,60,67,0.4)',
                          color: '#007AFF',
                          lineHeight: 1,
                        }}
                      >
                        {selected ? '✓' : ''}
                      </span>
                      <span className="text-[11px] font-semibold leading-5">
                        <TicketIdLabel ticketId={item.ticketId} />
                      </span>
                    </button>
                    <div className="flex-1 h-px" style={{ background: 'rgba(60,60,67,0.18)' }} />
                  </div>
                );
              })() : (() => {
                // Default-mode divider. Open tickets (in selectableTicketIds)
                // become buttons that enter multi-select on tap — that's how
                // users discover the feature without a dedicated Select button.
                // Closed dividers stay as plain visual context.
                const tappable = !!(
                  onTicketDividerTap && selectableTicketIds?.has(item.ticketId)
                );
                // Grey out closed-ticket chips so the user can tell them apart
                // from still-active ones at a glance — matters most when the
                // user expands a closed-group or when the no-open-tickets
                // skip guard leaves the whole history rendered inline.
                // When selectableTicketIds is undefined (e.g. Bayon embed,
                // which doesn't carry that info), keep today's blue: we have
                // no signal to mark anything closed.
                const isClosed = !!selectableTicketIds && !selectableTicketIds.has(item.ticketId);
                const chip = (
                  <span
                    className="px-3 py-0.5 rounded-full text-white text-[11px] font-semibold leading-5 select-none"
                    style={{
                      background: isClosed ? 'rgba(60,60,67,0.45)' : 'rgba(0,122,255,0.6)',
                      backdropFilter: 'blur(2px)',
                    }}
                  >
                    <TicketIdLabel ticketId={item.ticketId} />
                  </span>
                );
                // Phase 34: closed-ticket dividers become long-pressable when
                // the caller passes a reopen handler (L2 viewer in the
                // technician miniapp). Mutually exclusive with `tappable`
                // above — a divider is either OPEN (close-multi gesture) or
                // CLOSED (reopen gesture).
                const reopenable = isClosed && !!onTicketDividerLongPressForReopen;
                return (
                  <div className="flex items-center gap-2 px-4 py-3">
                    <div className="flex-1 h-px" style={{ background: 'rgba(60,60,67,0.18)' }} />
                    {tappable ? (
                      <LongPressDividerButton
                        ticketId={item.ticketId}
                        onLongPress={() => onTicketDividerTap!(item.ticketId)}
                      >
                        {chip}
                      </LongPressDividerButton>
                    ) : reopenable ? (
                      <LongPressDividerButton
                        ticketId={item.ticketId}
                        onLongPress={() => onTicketDividerLongPressForReopen!(item.ticketId)}
                      >
                        {chip}
                      </LongPressDividerButton>
                    ) : chip}
                    <div className="flex-1 h-px" style={{ background: 'rgba(60,60,67,0.18)' }} />
                  </div>
                );
              })()}

              {/* Per-divider 3-tier case context — only when external metadata exists */}
              {external ? (
                <div className="flex flex-col items-center gap-1.5 px-3 pb-2">
                  {/* Tier 1: "SV · Ref: <id> ↻" — left-aligned so the source label
                      always starts the line. The ref column is min-w-0 + break-all
                      (NOT flex-1, so the refresh icon hugs the ref instead of being
                      pushed to the far right): a long referenceId (e.g. the sv_gf_
                      base64 id) wraps WITHIN this column instead of detaching "SV ·"
                      or forcing horizontal scroll. */}
                  <div className="flex items-baseline gap-1.5 w-full text-[11px] text-gray-500">
                    <span className="font-semibold text-gray-700 shrink-0">{sourceLabel(external.externalSource)}</span>
                    <span aria-hidden className="shrink-0">·</span>
                    <span className="min-w-0 break-all font-mono">Ref: {external.externalReferenceId}</span>
                    {onRefreshTicketExternal ? (
                      <button
                        type="button"
                        onClick={() => onRefreshTicketExternal(item.ticketId)}
                        disabled={refreshingTicketIds?.has(item.ticketId)}
                        aria-label="Refresh case context"
                        className="shrink-0 text-[#007AFF] disabled:opacity-40 touch-active"
                      >
                        <RefreshCw
                          className={`h-3.5 w-3.5 ${refreshingTicketIds?.has(item.ticketId) ? 'animate-spin' : ''}`}
                          aria-hidden
                        />
                      </button>
                    ) : null}
                    {failedRefreshTicketIds?.has(item.ticketId) ? (
                      <AlertTriangle className="h-3 w-3 text-amber-500" aria-label="Refresh failed" />
                    ) : null}
                  </div>

                  {/* Case-type chip (Phase 36): shown for non-plain case shapes
                      (e.g. sv_gf → "Failed Activity"); keeps the SV source badge
                      in Tier 1. null for ordinary cases → nothing renders. */}
                  {(() => {
                    const chip = caseTypeChip(external.externalReferenceId);
                    return chip ? (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide">
                        {chip}
                      </span>
                    ) : null;
                  })()}

                  {/* Tier 2: title (centered, semibold, dark) */}
                  {external.externalTitle ? (
                    <div className="text-[13px] font-semibold text-gray-900 text-center max-w-[80%]">
                      {external.externalTitle}
                    </div>
                  ) : null}

                  {/* Tier 3: yellow sticker. When the server parsed structured
                      fields (Phase 36, plan 36-02), render a two-column
                      label/value list with the failure Reason promoted to a
                      highlighted ⚠ footer. Otherwise fall back BYTE-FOR-BYTE to
                      today's raw ReactMarkdown render of externalDescriptionMd
                      — the locked zero-regression guarantee for organic /
                      unparseable tickets. */}
                  <div className="max-w-[85%] px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200 shadow-sm">
                    {refreshingTicketIds?.has(item.ticketId) ? (
                      <p className="text-[12px] italic text-gray-500">Refreshing case context...</p>
                    ) : (
                      <>
                        {external.externalFields && external.externalFields.length > 0 ? (
                          // Field-list path — generic for SV / WL / sv_gf, no
                          // per-source branching, no Markdown. Labels are used
                          // verbatim (server already mapped IdNumber→"ID Number",
                          // Date of Birth→"DOB").
                          <div className="text-[12px] text-gray-800 leading-relaxed">
                            {/* Grid auto-sizes the label column to the widest label
                                (no clipping of long labels like "FailureSource") and
                                lets the value column wrap; min-w-0 + overflow-wrap
                                break unbreakable tokens (e.g. the Mismatch string) so
                                nothing overflows the box. */}
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                              {external.externalFields.map((field, i) => (
                                <Fragment key={i}>
                                  <span className="text-gray-500 whitespace-nowrap">{field.label}</span>
                                  <span className="min-w-0 font-medium text-gray-900 break-words [overflow-wrap:anywhere]">{field.value}</span>
                                </Fragment>
                              ))}
                            </div>
                            {external.externalReason ? (
                              <div className="mt-2 pt-2 border-t border-yellow-200">
                                <div className="flex items-start gap-1.5 px-2 py-1 rounded-md bg-amber-100 text-amber-900">
                                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
                                  <span className="min-w-0 font-semibold break-words [overflow-wrap:anywhere]">{external.externalReason}</span>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : external.externalDescriptionMd ? (
                          <div className="prose prose-sm max-w-none text-[12px] text-gray-800 leading-relaxed [&_p]:my-0 [&_strong]:font-semibold [&_strong]:text-gray-900">
                            <ReactMarkdown
                              allowedElements={['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'code']}
                              unwrapDisallowed
                              components={{
                                p: ({ children }) => <p className="my-0 whitespace-pre-line">{children}</p>,
                                a: ({ href, children }) => (
                                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#007AFF] underline">
                                    {children}
                                  </a>
                                ),
                              }}
                            >
                              {external.externalDescriptionMd}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-[12px] italic text-gray-500">Case context unavailable.</p>
                        )}
                        {/* SimVerifier initDataDetails person card — dynamic,
                            collapsed by default. Rendered OUTSIDE the field-list /
                            markdown-fallback branch so it always appears whenever
                            the passthrough carries ≥1 VISIBLE key (after denylist),
                            regardless of whether the case description parsed into
                            structured fields. */}
                        {external.externalInitData &&
                        typeof external.externalInitData === 'object' &&
                        hasVisibleInitData(external.externalInitData) ? (
                          <UserDetailsCard data={external.externalInitData} />
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          );
        }
        if (item.kind === 'closed-group') {
          const expanded = expandedGroups.has(item.key);
          const count = item.ticketIds.length;
          // Up to 2 IDs shown inline, the rest summarized as "+N more" —
          // keeps the line scannable on a narrow phone width while still
          // hinting which tickets are in the group.
          const PREVIEW = 2;
          const previewIds = item.ticketIds.slice(0, PREVIEW);
          const remaining = count - previewIds.length;
          const idsLabel =
            remaining > 0
              ? `${previewIds.join(', ')}, +${remaining} more`
              : previewIds.join(', ');
          const summary = expanded
            ? `Hide ${count} closed ${count === 1 ? 'ticket' : 'tickets'}`
            : `${count} ${count === 1 ? 'ticket' : 'tickets'} closed · ${idsLabel} · tap to show`;
          return (
            <div key={item.key} className="flex justify-center py-2">
              <button
                type="button"
                onClick={() => toggleGroup(item.key)}
                aria-expanded={expanded}
                aria-label={summary}
                className="px-3 py-1 rounded-full text-[11px] font-medium leading-5 select-none touch-active"
                style={{
                  background: 'rgba(60,60,67,0.08)',
                  color: 'rgba(60,60,67,0.75)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                {summary}
              </button>
            </div>
          );
        }
        if (item.kind === 'date') {
          return (
            <div key={item.key} className="flex justify-center py-3">
              <span
                className="px-3 py-0.5 rounded-full text-white text-[11px] font-semibold leading-5 select-none"
                style={{ background: 'rgba(52,52,52,0.5)', backdropFilter: 'blur(2px)' }}
              >
                {item.label}
              </span>
            </div>
          );
        }
        const msg = item.msg;
        const isFromUser = msg.from === 'user';
        // Identity-based when viewerDeviceId is provided: only THIS viewer's
        // own technician messages go right. End-user messages and OTHER
        // technicians' messages both go left, each with their own sender label.
        // Falls back to role-based (msg.from !== 'user') when viewerDeviceId
        // is empty — preserves today's single-technician behavior and the
        // Bayon embed adapter's pre-existing pattern.
        const isMine = viewerDeviceId
          ? msg.from === 'technician' && String(msg.technicianId ?? '') === viewerDeviceId
          : !isFromUser;
        const callerLabel = getSenderLabel ? getSenderLabel(msg, isFromUser) : undefined;
        // v4.1: wrap bubble in a long-press / selection-toggle button only
        // when the caller actually opted in (technician miniapp passes
        // these props; Bayon embed doesn't). Keeps the Bayon code path
        // visually identical and zero-cost.
        const supportsDelete = !!onMessageLongPress || !!onToggleMessageSelection;
        const supportsSwipeReply = !!onMessageSwipeReply;
        // Phase 33: a row is "replyable" iff it carries the Telegram-side
        // anchor we need to attach as reply_parameters. Mirrors the
        // MessageActionModal `replyable` gate; gates BOTH the modal Reply
        // button visibility AND the swipe-left-to-reply gesture.
        const isReplyable = msg.telegramMessageId != null;
        const isSelected = !!selectedMessageIds?.has(msg.id);
        // v4.1 strict policy: a row is selectable for delete iff it can
        // also be unsent on Telegram. Mirrors the server-side eligibility
        // pre-check at DELETE /messages, so the UI never builds a batch
        // the server would reject. Soft-deleted rows (deletedForEveryone
        // placeholders) are also non-selectable — already gone.
        const within48h =
          Date.now() - new Date(msg.timestamp).getTime() <= 48 * 60 * 60 * 1000;
        const isSelectable =
          msg.from === 'technician' &&
          msg.telegramMessageId != null &&
          within48h &&
          !msg.deletedForEveryone;
        // Phase 39 — broadcast/announcement messages render as a centered,
        // full-width card (like the case-reference card) instead of a chat
        // bubble, so a technician clearly reads it as a system announcement
        // rather than a colleague's reply. Not selectable/deletable — it's a
        // record of what the broadcast sent. Structured so a title + media
        // thumbnail can slot in later without a rethink.
        if (msg.isBroadcast) {
          const ts = new Date(msg.timestamp);
          const hhmm = `${String(ts.getHours()).padStart(2, '0')}:${String(ts.getMinutes()).padStart(2, '0')}`;
          const ann = parseAnnouncement(msg.text ?? '');
          const thumb = ann.link ? youtubeThumbnail(ann.link) : null;
          return (
            <div key={msg.id ?? item.origIdx} className="px-2 sm:px-3 py-2">
              <div className="rounded-xl border border-orange-200 bg-white overflow-hidden shadow-sm">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-500">
                  <Megaphone className="h-3.5 w-3.5 text-white shrink-0" strokeWidth={2.5} aria-hidden />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-white">
                    Announcement
                  </span>
                  <span className="ml-auto text-[11px] font-medium text-white/90">{hhmm}</span>
                </div>
                <div className="px-3.5 py-3 space-y-2.5">
                  {/* Telegram order: thumbnail → title → message → link */}
                  {thumb && ann.link && (
                    <a href={ann.link} target="_blank" rel="noopener noreferrer" className="block">
                      <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-black/5">
                        <img
                          src={thumb}
                          alt=""
                          loading="lazy"
                          className="w-full aspect-video object-cover"
                        />
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55">
                            <Play className="h-5 w-5 text-white translate-x-[1px]" fill="currentColor" aria-hidden />
                          </span>
                        </span>
                      </div>
                    </a>
                  )}
                  {ann.title && (
                    <p className="text-[15px] font-semibold text-gray-900 leading-snug break-words">
                      {ann.title}
                    </p>
                  )}
                  {(ann.body || ann.link) && (
                    <p className="text-[14px] leading-relaxed text-gray-700 whitespace-pre-wrap break-words">
                      {ann.body}
                      {ann.link && (
                        <>
                          {ann.body ? ' ' : ''}
                          <a
                            href={ann.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-blue-600 active:opacity-70"
                          >
                            [{ann.label}]
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        }
        const bubble = (
          <MessageBubble
            key={msg.id ?? item.origIdx}
            message={msg}
            isMine={isMine}
            senderLabel={callerLabel}
            userFirstName={userFirstName}
            showLabelOnMine={showLabelOnMine ?? !!viewerDeviceId}
            onReplyTargetClick={onJumpToMessage}
          />
        );
        if (!supportsDelete && !supportsSwipeReply) return bubble;
        return (
          <SelectableMessageWrapper
            key={msg.id ?? item.origIdx}
            messageId={msg.id}
            selectionMode={messageSelectionMode}
            isSelectable={isSelectable}
            isSelected={isSelected}
            isReplyable={isReplyable}
            onLongPress={
              onMessageLongPress
                ? (rect, html) => onMessageLongPress(msg, rect, html, isMine)
                : undefined
            }
            onToggleSelection={onToggleMessageSelection ? () => onToggleMessageSelection(msg.id) : undefined}
            onSwipeReply={onMessageSwipeReply ? () => onMessageSwipeReply(msg) : undefined}
          >
            {bubble}
          </SelectableMessageWrapper>
        );
      })}
    </div>
  );
}
